import React, { useState, useEffect } from 'react';
import { PageShell, StatCard } from '../../components/Ui';
import { MdBloodtype, MdPerson, MdInventory2, MdWarning, MdCheckCircle } from 'react-icons/md';
import { useHmsRecords, useHmsConfig } from '../../hooks/useHmsRecords';
import { ipApi } from '../../services/api';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

/** ABO/Rh compatible donor groups for a recipient (simplified clinical table). */
const COMPATIBLE_DONORS = {
  'O-': ['O-'],
  'O+': ['O-', 'O+'],
  'A-': ['O-', 'A-'],
  'A+': ['O-', 'O+', 'A-', 'A+'],
  'B-': ['O-', 'B-'],
  'B+': ['O-', 'O+', 'B-', 'B+'],
  'AB-': ['O-', 'A-', 'B-', 'AB-'],
  'AB+': ['O-', 'O+', 'A-', 'A+', 'B-', 'B+', 'AB-', 'AB+'],
};

export default function BloodBankPage() {
  const { records: donors, create: createDonor, reload: reloadDonors } = useHmsRecords('bloodbank-donor');
  const { records: requests, create: createRequest, patch: patchRequest, reload: reloadRequests } = useHmsRecords('bloodbank-request');
  const { records: crossMatches, create: createXm, patch: patchXm, reload: reloadXm } = useHmsRecords('bloodbank-crossmatch');
  const { config, save: saveConfig, loading: invLoading } = useHmsConfig('bloodbank-inventory');
  const [inventory, setInventory] = useState([]);
  const [tab, setTab] = useState('inventory');
  const [donorForm, setDonorForm] = useState({ name: '', group: 'O+', phone: '', lastDonation: '' });
  const [reqForm, setReqForm] = useState({ patient: '', uhid: '', group: 'O+', units: 1, reason: '', reqBy: '' });
  const [xmForm, setXmForm] = useState({
    patient: '', uhid: '', patientGroup: 'O+', donorGroup: 'O+', donorId: '', bagNo: '',
    technique: 'Saline + AHG', result: 'Compatible', notes: '',
  });
  const [showDonorForm, setShowDonorForm] = useState(false);
  const [showReqForm, setShowReqForm] = useState(false);
  const [showXmForm, setShowXmForm] = useState(false);
  const [savingInv, setSavingInv] = useState(false);

  useEffect(() => {
    if (config?.inventory?.length) {
      setInventory(config.inventory);
      return;
    }
    // First open / missing config → seed all 8 groups so the module is usable
    if (!invLoading) {
      setInventory(BLOOD_GROUPS.map((group) => ({ group, units: 0, reserved: 0 })));
    }
  }, [config, invLoading]);

  const totalUnits = inventory.reduce((s, i) => s + Number(i.units || 0), 0);
  const lowStock = inventory.filter((i) => i.units <= 3).length;
  const pendingReq = requests.filter((r) => r.status === 'Pending').length;

  const addDonor = async (e) => {
    e.preventDefault();
    try {
      const lastDate = new Date(donorForm.lastDonation);
      const daysSince = donorForm.lastDonation ? Math.floor((Date.now() - lastDate.getTime()) / 86400000) : 999;
      await createDonor({ ...donorForm, eligible: daysSince > 84 });
      setShowDonorForm(false);
      setDonorForm({ name: '', group: 'O+', phone: '', lastDonation: '' });
      reloadDonors();
    } catch (err) {
      window.alert(err.message || 'Failed to register donor');
    }
  };

  const addRequest = async (e) => {
    e.preventDefault();
    try {
      await createRequest({ ...reqForm, status: 'Pending', date: new Date().toISOString().slice(0, 10) });
      setShowReqForm(false);
      setReqForm({ patient: '', uhid: '', group: 'O+', units: 1, reason: '', reqBy: '' });
      reloadRequests();
    } catch (err) {
      window.alert(err.message || 'Failed to submit blood request');
    }
  };

  const addCrossMatch = async (e) => {
    e.preventDefault();
    try {
      const allowed = COMPATIBLE_DONORS[xmForm.patientGroup] || [];
      const aboOk = allowed.includes(xmForm.donorGroup);
      await createXm({
        ...xmForm,
        aboCompatible: aboOk,
        date: new Date().toISOString().slice(0, 10),
        status: xmForm.result === 'Compatible' && aboOk ? 'Cleared' : 'Hold',
      });
      setShowXmForm(false);
      setXmForm({
        patient: '', uhid: '', patientGroup: 'O+', donorGroup: 'O+', donorId: '', bagNo: '',
        technique: 'Saline + AHG', result: 'Compatible', notes: '',
      });
      reloadXm();
    } catch (err) {
      window.alert(err.message || 'Failed to save cross-match');
    }
  };

  const issueBlood = async (id) => {
    const req = requests.find((r) => r.id === id || r.recordId === id);
    if (!req) return;
    try {
      const res = await ipApi.issueBloodRequest(req.recordId || id);
      if (res?.inventory) setInventory(res.inventory);
      else {
        await reloadRequests();
        const cfg = await ipApi.getHmsConfig('bloodbank-inventory');
        if (cfg?.config?.inventory) setInventory(cfg.config.inventory);
      }
      reloadRequests();
    } catch (e) {
      window.alert(e.message || 'Failed to issue blood');
    }
  };

  const updateInv = (group, val) => {
    setInventory((inv) => inv.map((i) => (i.group === group ? { ...i, units: Math.max(0, Number(val) || 0) } : i)));
  };

  const saveInventory = async () => {
    setSavingInv(true);
    try {
      await saveConfig({ inventory });
    } finally {
      setSavingInv(false);
    }
  };

  return (
    <PageShell
      title="Blood Bank"
      subtitle="Inventory · Donors · Cross-matching · Issue requests"
      actions={
        <div className="row-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setShowDonorForm((s) => !s)}>+ Register Donor</button>
          <button type="button" className="btn btn-secondary" onClick={() => { setShowXmForm((s) => !s); setShowReqForm(false); }}>+ Cross-match</button>
          <button type="button" className="btn btn-primary" onClick={() => setShowReqForm((s) => !s)}>+ Blood Request</button>
        </div>
      }
    >
      {/* Stats */}
      <div className="stat-grid">
        <StatCard label="Total Units" value={totalUnits} accent="#dc2626" icon={MdBloodtype} />
        <StatCard label="Blood Groups" value={`${inventory.filter((i) => i.units > 0).length}/8`} accent="#0d6e6a" icon={MdInventory2} />
        <StatCard label="Low Stock Alerts" value={lowStock} accent="#f97316" icon={MdWarning} />
        <StatCard label="Pending Requests" value={pendingReq} accent="#8b5cf6" icon={MdPerson} />
        <StatCard label="Registered Donors" value={donors.length} accent="#10b981" icon={MdCheckCircle} />
        <StatCard label="Eligible Donors" value={donors.filter((d) => d.eligible).length} accent="#06b6d4" icon={MdPerson} />
      </div>

      {/* Forms */}
      {showDonorForm && (
        <form className="card mb form-grid" onSubmit={addDonor}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Register Blood Donor</h3>
          <div className="field"><label>Donor name *</label><input className="input" required value={donorForm.name} onChange={(e) => setDonorForm({ ...donorForm, name: e.target.value })} /></div>
          <div className="field"><label>Blood group *</label>
            <select className="input" value={donorForm.group} onChange={(e) => setDonorForm({ ...donorForm, group: e.target.value })}>
              {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="field"><label>Phone</label><input className="input" value={donorForm.phone} onChange={(e) => setDonorForm({ ...donorForm, phone: e.target.value })} /></div>
          <div className="field"><label>Last donation date</label><input type="date" className="input" value={donorForm.lastDonation} onChange={(e) => setDonorForm({ ...donorForm, lastDonation: e.target.value })} /></div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowDonorForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Register</button>
          </div>
        </form>
      )}

      {showReqForm && (
        <form className="card mb form-grid" onSubmit={addRequest}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Blood Request</h3>
          <div className="field"><label>Patient *</label><input className="input" required value={reqForm.patient} onChange={(e) => setReqForm({ ...reqForm, patient: e.target.value })} /></div>
          <div className="field"><label>UHID</label><input className="input" value={reqForm.uhid} onChange={(e) => setReqForm({ ...reqForm, uhid: e.target.value })} /></div>
          <div className="field"><label>Blood group *</label>
            <select className="input" value={reqForm.group} onChange={(e) => setReqForm({ ...reqForm, group: e.target.value })}>
              {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="field"><label>Units required</label><input type="number" min={1} className="input" value={reqForm.units} onChange={(e) => setReqForm({ ...reqForm, units: Number(e.target.value) })} /></div>
          <div className="field"><label>Reason</label><input className="input" value={reqForm.reason} onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} /></div>
          <div className="field"><label>Requested by</label><input className="input" value={reqForm.reqBy} onChange={(e) => setReqForm({ ...reqForm, reqBy: e.target.value })} /></div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowReqForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Submit Request</button>
          </div>
        </form>
      )}

      {showXmForm && (
        <form className="card mb form-grid" onSubmit={addCrossMatch}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Cross-matching</h3>
          <p className="hint" style={{ gridColumn: '1/-1', margin: 0 }}>
            Records major/minor compatibility before issue. ABO/Rh table warns if donor group is not theoretically compatible.
          </p>
          <div className="field"><label>Patient *</label><input className="input" required value={xmForm.patient} onChange={(e) => setXmForm({ ...xmForm, patient: e.target.value })} /></div>
          <div className="field"><label>UHID</label><input className="input" value={xmForm.uhid} onChange={(e) => setXmForm({ ...xmForm, uhid: e.target.value })} /></div>
          <div className="field"><label>Patient group *</label>
            <select className="input" value={xmForm.patientGroup} onChange={(e) => setXmForm({ ...xmForm, patientGroup: e.target.value })}>
              {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="field"><label>Donor / bag group *</label>
            <select className="input" value={xmForm.donorGroup} onChange={(e) => setXmForm({ ...xmForm, donorGroup: e.target.value })}>
              {BLOOD_GROUPS.map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          {!(COMPATIBLE_DONORS[xmForm.patientGroup] || []).includes(xmForm.donorGroup) && (
            <div style={{ gridColumn: '1/-1', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
              ⚠ ABO/Rh: {xmForm.donorGroup} is not a standard compatible donor for recipient {xmForm.patientGroup}. Preferred: {(COMPATIBLE_DONORS[xmForm.patientGroup] || []).join(', ')}.
            </div>
          )}
          <div className="field"><label>Bag / unit no.</label><input className="input" value={xmForm.bagNo} onChange={(e) => setXmForm({ ...xmForm, bagNo: e.target.value })} /></div>
          <div className="field"><label>Donor (optional)</label>
            <select className="input" value={xmForm.donorId} onChange={(e) => {
              const d = donors.find((x) => (x.recordId || x.id) === e.target.value);
              setXmForm({ ...xmForm, donorId: e.target.value, donorGroup: d?.group || xmForm.donorGroup });
            }}>
              <option value="">— Select registered donor —</option>
              {donors.map((d) => <option key={d.recordId || d.id} value={d.recordId || d.id}>{d.name} ({d.group})</option>)}
            </select>
          </div>
          <div className="field"><label>Technique</label>
            <select className="input" value={xmForm.technique} onChange={(e) => setXmForm({ ...xmForm, technique: e.target.value })}>
              {['Saline + AHG', 'Immediate spin', 'Gel card', 'Electronic XM'].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Lab result *</label>
            <select className="input" value={xmForm.result} onChange={(e) => setXmForm({ ...xmForm, result: e.target.value })}>
              <option>Compatible</option>
              <option>Incompatible</option>
              <option>Inconclusive</option>
            </select>
          </div>
          <div className="field full"><label>Notes</label><input className="input" value={xmForm.notes} onChange={(e) => setXmForm({ ...xmForm, notes: e.target.value })} placeholder="Antibody screen, special requirements…" /></div>
          <div className="form-actions full">
            <button type="button" className="btn btn-secondary" onClick={() => setShowXmForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save cross-match</button>
          </div>
        </form>
      )}

      {/* Tabs */}
      <div className="period-tabs card mb">
        {[
          { id: 'inventory', label: '🩸 Blood Inventory' },
          { id: 'crossmatch', label: `🔬 Cross-match (${crossMatches.length})` },
          { id: 'requests', label: `📋 Requests (${pendingReq} pending)` },
          { id: 'donors', label: `👤 Donors (${donors.length})` },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'inventory' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Blood Stock Inventory</h3>
            <button type="button" className="btn btn-primary btn-sm" disabled={savingInv || invLoading} onClick={saveInventory}>{savingInv ? 'Saving…' : 'Save inventory'}</button>
          </div>
          <p className="hint mb">Update units and save. Highlighted in red = low stock (≤3 units).</p>
          <div className="bb-inventory-grid">
            {inventory.map((item) => (
              <div key={item.group} className="bb-group-card" style={{ borderColor: item.units <= 3 ? '#fca5a5' : item.units <= 8 ? '#fde68a' : '#bbf7d0', background: item.units <= 3 ? '#fef2f2' : item.units <= 8 ? '#fefce8' : '#f0fdf4' }}>
                <div className="bb-group-label" style={{ color: '#dc2626', fontSize: 28, fontWeight: 900, fontFamily: 'Outfit, sans-serif' }}>{item.group}</div>
                <div className="bb-group-units">
                  <input type="number" min={0} className="input" style={{ width: 70, textAlign: 'center', fontSize: 18, fontWeight: 700 }} value={item.units} onChange={(e) => updateInv(item.group, e.target.value)} />
                  <div className="hint" style={{ fontSize: 11 }}>units</div>
                </div>
                {item.reserved > 0 && <div className="bb-reserved">{item.reserved} reserved</div>}
                {item.units <= 3 && <div className="bb-low-warning">⚠ LOW</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'crossmatch' && (
        <div className="card">
          <h3>Cross-match log</h3>
          <p className="hint mb">Compatible + ABO-ok records are marked Cleared for issue. Incompatible or ABO-mismatch stay on Hold.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {crossMatches.map((xm) => {
              const ok = xm.result === 'Compatible' && xm.aboCompatible !== false;
              return (
                <div key={xm.recordId || xm.id} className="bb-request-card" style={{ borderLeft: `4px solid ${ok ? '#10b981' : '#ef4444'}` }}>
                  <div className="bb-req-head">
                    <div>
                      <div className="bb-req-patient">{xm.patient} <span className="hint">{xm.uhid}</span></div>
                      <div className="bb-req-detail">
                        <span className="bb-group-badge">Pt {xm.patientGroup}</span>
                        <span className="bb-group-badge">Bag {xm.donorGroup}</span>
                        <span>{xm.technique}</span>
                        {xm.bagNo && <span>Bag #{xm.bagNo}</span>}
                        <span>📅 {xm.date}</span>
                      </div>
                      {xm.notes && <div className="hint" style={{ marginTop: 4 }}>{xm.notes}</div>}
                      {xm.aboCompatible === false && (
                        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>⚠ ABO/Rh theoretically incompatible</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                      <span className="ot-status-chip" style={{
                        background: xm.result === 'Compatible' ? '#dcfce7' : '#fef2f2',
                        color: xm.result === 'Compatible' ? '#16a34a' : '#dc2626',
                        border: `1px solid ${xm.result === 'Compatible' ? '#86efac' : '#fecaca'}`,
                      }}>{xm.result}</span>
                      <span className="hint">{xm.status || (ok ? 'Cleared' : 'Hold')}</span>
                      {xm.status !== 'Issued' && ok && (
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => patchXm(xm.recordId || xm.id, { status: 'Issued' }).then(reloadXm)}>
                          Mark issued
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {crossMatches.length === 0 && <p className="hint table-empty">No cross-matches recorded yet.</p>}
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <div className="card">
          <h3>Blood Issue Requests</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requests.map((r) => {
              const avail = inventory.find((i) => i.group === r.group)?.units || 0;
              const canIssue = r.status === 'Pending' && avail >= r.units;
              return (
                <div key={r.id} className="bb-request-card" style={{ borderLeft: `4px solid ${r.status === 'Issued' ? '#10b981' : canIssue ? '#3b82f6' : '#ef4444'}` }}>
                  <div className="bb-req-head">
                    <div>
                      <div className="bb-req-patient">{r.patient} <span className="hint">{r.uhid}</span></div>
                      <div className="bb-req-detail">
                        <span className="bb-group-badge">{r.group}</span>
                        <span>{r.units} unit(s) · {r.reason}</span>
                        <span>👨‍⚕️ {r.reqBy}</span>
                        <span>📅 {r.date}</span>
                      </div>
                      {!canIssue && r.status === 'Pending' && (
                        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>⚠ Only {avail} unit(s) of {r.group} available</div>
                      )}
                    </div>
                    <span className="ot-status-chip" style={{ background: r.status === 'Issued' ? '#dcfce7' : '#eff6ff', color: r.status === 'Issued' ? '#16a34a' : '#2563eb', border: `1px solid ${r.status === 'Issued' ? '#86efac' : '#bfdbfe'}` }}>{r.status}</span>
                  </div>
                  {r.status === 'Pending' && (
                    <div className="ot-card-actions">
                      <button type="button" className="btn btn-sm btn-primary" disabled={!canIssue} onClick={() => issueBlood(r.id)}>Issue Blood</button>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => patchRequest(r.recordId || r.id, { status: 'Cancelled' })}>Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
            {requests.length === 0 && <p className="hint table-empty">No blood requests yet.</p>}
          </div>
        </div>
      )}

      {tab === 'donors' && (
        <div className="card">
          <h3>Donor Registry</h3>
          <p className="hint mb">Donors eligible to donate again (84+ days since last donation) shown in green.</p>
          <div className="bb-donors-grid">
            {donors.map((d) => (
              <div key={d.id} className="bb-donor-card" style={{ borderTop: `3px solid ${d.eligible ? '#10b981' : '#f97316'}` }}>
                <div className="bb-donor-avatar" style={{ background: d.eligible ? '#10b981' : '#f97316' }}>{(d.name || 'D').charAt(0)}</div>
                <div>
                  <div className="bb-donor-name">{d.name}</div>
                  <div className="bb-donor-meta">
                    <span className="bb-group-badge" style={{ fontSize: 12 }}>{d.group}</span>
                    <span>{d.phone}</span>
                  </div>
                  <div className="bb-donor-last">Last donation: {d.lastDonation || 'Unknown'}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: d.eligible ? '#16a34a' : '#f97316' }}>
                    {d.eligible ? '✓ Eligible to donate' : '⏳ Not yet eligible'}
                  </span>
                </div>
              </div>
            ))}
            {donors.length === 0 && <p className="hint table-empty">No donors registered.</p>}
          </div>
        </div>
      )}
    </PageShell>
  );
}
